/**
 * JFX Wunschliste - Seitenlogik fuer /pages/wunschliste
 * ---------------------------------------------------------------------------
 * Liest die gespeicherten Produkt-Handles aus dem Wishlist-Kernmodul und laesst
 * die Produktdarstellung serverseitig von Shopify rendern (Section Rendering
 * API). Dadurch sind Titel, Preis, Vergleichspreis, Bild und Verfuegbarkeit
 * immer aktuell und folgen exakt der Preis-Logik des Themes (inkl. VPE).
 *
 * Produkte, die es nicht mehr gibt (geloescht oder Handle geaendert), werden
 * kontrolliert aus der lokalen Liste entfernt.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CHUNK_SIZE = 20;   // Shopify erlaubt max. 20 all_products-Zugriffe pro Anfrage

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function chunk(list, size) {
    var out = [];
    for (var i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
    return out;
  }

  var NO_STORAGE_NOTE = 'Dein Browser speichert die Wunschliste nicht. Sie gilt daher nur für diese Sitzung.';

  function WishlistPage(root) {
    this.root = root;
    this.searchUrl = root.getAttribute('data-search-url') || '/search';
    this.sectionId = root.getAttribute('data-section-id') || 'jfx-wishlist-items';
    this.grid = root.querySelector('[data-jfx-wl-grid]');
    this.empty = root.querySelector('[data-jfx-wl-empty]');
    this.loading = root.querySelector('[data-jfx-wl-loading]');
    this.notice = root.querySelector('[data-jfx-wl-notice]');
    this.countEl = root.querySelector('[data-jfx-wl-page-count]');
    this.busy = false;
    this.pendingRender = false;
    this.lastSignature = null;

    this.onWishlistUpdated = this.onWishlistUpdated.bind(this);
    this.onClick = this.onClick.bind(this);

    document.addEventListener('wishlist:updated', this.onWishlistUpdated);
    this.root.addEventListener('click', this.onClick);

    this.render();
  }

  WishlistPage.prototype.onWishlistUpdated = function (evt) {
    var action = evt && evt.detail ? evt.detail.action : null;
    this.updateCount();

    // Entfernen wird direkt im DOM abgebildet -> kein Neuladen noetig.
    if (action === 'remove' && evt.detail && evt.detail.productId) {
      var node = this.grid && this.grid.querySelector('[data-product-id="' + evt.detail.productId + '"]');
      if (node) node.remove();
      this.lastSignature = window.JFXWishlist ? window.JFXWishlist.getHandles().join(',') : null;
      if (this.grid && !this.grid.children.length) this.showEmpty(true);
      return;
    }

    if (action === 'prune') return;   // bereits im DOM abgeraeumt
    this.render();
  };

  WishlistPage.prototype.onClick = function (evt) {
    var removeBtn = evt.target && evt.target.closest ? evt.target.closest('[data-jfx-wl-remove]') : null;
    if (!removeBtn) return;
    evt.preventDefault();
    var id = removeBtn.getAttribute('data-product-id');
    if (window.JFXWishlist) window.JFXWishlist.remove(id, 'wishlist-page');
  };

  WishlistPage.prototype.updateCount = function () {
    if (!this.countEl || !window.JFXWishlist) return;
    var count = window.JFXWishlist.count();
    this.countEl.textContent = count === 1
      ? '1 Produkt gespeichert'
      : count + ' Produkte gespeichert';
  };

  WishlistPage.prototype.showEmpty = function (isEmpty) {
    if (this.empty) this.empty.hidden = !isEmpty;
    if (this.grid) this.grid.hidden = isEmpty;
  };

  WishlistPage.prototype.setLoading = function (isLoading) {
    if (this.loading) this.loading.hidden = !isLoading;
  };

  WishlistPage.prototype.setNotice = function (message) {
    if (!this.notice) return;
    this.notice.textContent = message || '';
    this.notice.hidden = !message;
  };

  WishlistPage.prototype.fetchChunk = function (handles) {
    var url = this.searchUrl
      + (this.searchUrl.indexOf('?') > -1 ? '&' : '?')
      + 'section_id=' + encodeURIComponent(this.sectionId)
      + '&type=product'
      + '&q=' + encodeURIComponent(handles.join(' '));

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      });
  };

  WishlistPage.prototype.render = function () {
    var self = this;

    if (!window.JFXWishlist) return;
    if (this.busy) { this.pendingRender = true; return; }

    var items = window.JFXWishlist.getItems();
    var handles = items.map(function (i) { return i.handle; }).filter(Boolean);
    var signature = handles.join(',');
    var storageNote = window.JFXWishlist.storageAvailable ? '' : NO_STORAGE_NOTE;

    this.updateCount();

    if (!handles.length) {
      this.lastSignature = signature;
      this.setLoading(false);
      this.setNotice(storageNote);
      if (this.grid) this.grid.innerHTML = '';
      this.showEmpty(true);
      return;
    }

    if (signature === this.lastSignature && this.grid && this.grid.children.length) {
      this.showEmpty(false);
      return;
    }

    this.busy = true;
    this.lastSignature = signature;
    this.showEmpty(false);
    this.setLoading(true);
    this.setNotice(storageNote);

    var groups = chunk(handles, CHUNK_SIZE);

    Promise.all(groups.map(function (group) { return self.fetchChunk(group); }))
      .then(function (htmlParts) {
        var parser = new DOMParser();
        var byHandle = {};
        var missing = [];

        htmlParts.forEach(function (html) {
          var doc = parser.parseFromString(html, 'text/html');
          var nodes = doc.querySelectorAll('[data-jfx-wl-item]');
          for (var i = 0; i < nodes.length; i += 1) {
            var handle = nodes[i].getAttribute('data-product-handle');
            if (nodes[i].hasAttribute('data-jfx-wl-missing')) {
              missing.push(handle);
            } else {
              byHandle[handle] = nodes[i];
            }
          }
        });

        var fragment = document.createDocumentFragment();
        handles.forEach(function (handle) {
          if (byHandle[handle]) fragment.appendChild(document.importNode(byHandle[handle], true));
        });

        if (self.grid) {
          self.grid.innerHTML = '';
          self.grid.appendChild(fragment);
        }

        self.setLoading(false);

        if (missing.length && window.JFXWishlist.prune) {
          var removed = window.JFXWishlist.prune(missing);
          if (removed > 0) {
            self.lastSignature = window.JFXWishlist.getHandles().join(',');
            self.setNotice(removed === 1
              ? 'Ein Produkt ist nicht mehr verfügbar und wurde von der Wunschliste entfernt.'
              : removed + ' Produkte sind nicht mehr verfügbar und wurden von der Wunschliste entfernt.');
          }
        }

        self.updateCount();
        if (self.grid && !self.grid.children.length) self.showEmpty(true);
        if (window.JFXWishlist.syncButtons) window.JFXWishlist.syncButtons(self.grid || document);
      })
      .catch(function (error) {
        self.setLoading(false);
        self.lastSignature = null;
        self.setNotice('Die Wunschliste konnte gerade nicht geladen werden. Bitte lade die Seite neu.');
        // eslint-disable-next-line no-console
        console.warn('[JFX Wunschliste]', error);
      })
      .then(function () {
        self.busy = false;
        if (self.pendingRender) {
          self.pendingRender = false;
          self.render();
        }
      });
  };

  function boot() {
    var root = document.getElementById('jfx-wishlist-app');
    if (!root || root.getAttribute('data-jfx-wl-booted') === 'true') return;
    root.setAttribute('data-jfx-wl-booted', 'true');

    if (window.JFXWishlist && window.JFXWishlist.ready) {
      new WishlistPage(root);
    } else {
      document.addEventListener('wishlist:updated', function onceReady(evt) {
        if (!evt.detail || evt.detail.action !== 'init') return;
        document.removeEventListener('wishlist:updated', onceReady);
        new WishlistPage(root);
      });
    }
  }

  ready(boot);

  // Theme-Editor: Section wird neu geladen
  document.addEventListener('shopify:section:load', function (evt) {
    if (!evt.target || !evt.target.querySelector) return;
    if (evt.target.querySelector('#jfx-wishlist-app')) boot();
  });
}());
